/**
 * CargoWise (TMS) rollout scope.
 *
 * Import this as `@pelicargo/utils/cargowise`, not through the package barrel. The barrel
 * re-exports `commodities`, which imports airline typings back out of `api/src` — so an
 * api module that pulls the barrel at runtime closes a cycle and dies on an undefined
 * namespace. This file imports nothing, so the subpath is always safe.
 *
 * The integration is live for a fixed set of fulfillment partners. Both sides need the
 * rule — the API to decide whether a shipment may be pushed or pulled at all, and the
 * admin to decide whether to show the CargoWise controls — so it lives here rather than
 * being written twice and drifting.
 *
 * The allowlist itself is not baked in here: it is the `cargowise.fulfillmentPartnerAccountIds`
 * Peli config value, so onboarding a partner is a config edit rather than a deploy. This
 * file only owns the parsing and the predicate; the API registers the value and the admin
 * reads it back through `usePeliConfig('cargowise')`.
 *
 * It is an allowlist: a shipment whose quote has no fulfillment partner configuration
 * is out of scope, same as one belonging to a partner that is not listed.
 */

/** Seed value for the config, and what applies until an admin overrides it. */
export const CARGOWISE_DEFAULT_FP_ACCOUNT_IDS = '626,715';

/**
 * Read the configured allowlist. It is stored as a comma-separated string because the
 * config table holds plain strings; anything that is not a positive integer is dropped
 * rather than parsed to NaN, so a typo narrows the rollout instead of widening it.
 */
export const parseCargoWiseFpAccountIds = (value?: string | null): number[] =>
  (value ?? '')
    .split(',')
    .map((part) => Number(part.trim()))
    .filter((id) => Number.isInteger(id) && id > 0);

/** The shape both sides have to hand: a shipment form (or quote) with its FPC loaded. */
export type CargoWiseScopedQuote = {
  fulfillment_partner_configuration?: { account_as_fulfillment_partner_id: number } | null;
} | null;

export const isCargoWiseFulfillmentPartner = (
  quote: CargoWiseScopedQuote | undefined,
  allowedAccountIds: number[],
): boolean => {
  const accountId = quote?.fulfillment_partner_configuration?.account_as_fulfillment_partner_id;
  return accountId != null && allowedAccountIds.includes(accountId);
};
